'use client';

import Image from 'next/image';
import { ArrowUpRight, Plus, Star, MapPin, Building2, Volume2, VolumeX } from 'lucide-react';
import { motion } from 'framer-motion';
import { useState, useRef } from 'react';
import Link from 'next/link';
import { useVideoLazyLoad } from '@/hooks/useVideoLazyLoad';

export default function Hero() {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isMuted, setIsMuted] = useState(true);
    useVideoLazyLoad(videoRef);

    const toggleMute = () => {
        if (videoRef.current) {
            videoRef.current.muted = !videoRef.current.muted;
            setIsMuted(videoRef.current.muted);
        }
    };

    return (
        <section className="relative overflow-hidden bg-background pt-8 pb-12 lg:pt-4 lg:pb-24 px-4 sm:px-6 md:px-12">



            <div className="max-w-7xl mx-auto flex flex-col gap-8 md:gap-12 lg:gap-16">

                {/* Header Section (Full Width) */}
                <div className="flex flex-col items-center lg:items-start text-center lg:text-left mt-4 gap-4 lg:gap-2">
                    <h1 className="text-4xl sm:text-6xl md:text-7xl lg:text-[5.5rem] font-semibold leading-[1.1] tracking-tight text-foreground">
                        <span className="text-primary">Hassle Free Property Management</span> for
                    </h1>

                    <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-2 sm:gap-x-4 lg:gap-x-8 gap-y-3 w-full">
                        <div className="flex flex-wrap justify-center items-center gap-2 sm:gap-3">
                            <span className="px-4 sm:px-5 lg:px-6 py-2 lg:py-2.5 rounded-full bg-secondary text-secondary-foreground text-xs sm:text-sm lg:text-base font-medium">
                                <Building2 size={14} className="inline mr-1.5 sm:w-4 sm:h-4" />
                                Villas
                            </span>
                            <span className="px-4 sm:px-5 lg:px-6 py-2 lg:py-2.5 rounded-full bg-secondary text-secondary-foreground text-xs sm:text-sm lg:text-base font-medium">Apartments</span>
                            <span className="px-4 sm:px-5 lg:px-6 py-2 lg:py-2.5 rounded-full bg-secondary text-secondary-foreground text-xs sm:text-sm lg:text-base font-medium flex items-center gap-2 shadow-sm">
                                <MapPin size={14} className="sm:w-4 sm:h-4" />
                                Dubai
                            </span>
                        </div>

                        <h2 className="text-4xl sm:text-6xl md:text-7xl lg:text-[5.5rem] font-semibold leading-[1.1] tracking-tight text-foreground">
                            Your Home<span className="text-primary">.</span>
                        </h2>
                    </div>
                </div>

                {/* Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 lg:gap-20 items-end">

                    {/* Left Column: Description & CTAs */}
                    <div className="flex flex-col gap-6 md:gap-8 lg:pb-8 relative z-10 text-center lg:text-left order-2 lg:order-1">
                        <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-lg leading-relaxed mx-auto lg:mx-0">
                            Why partner with HOPO Homes? We provide up to 30% more rental yield, on-time rent payments, and handle all property management and maintenance.
                        </p>

                        <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 sm:gap-6">
                            <Link href="/owner#owner-consultation-form">
                                <button className="w-full sm:w-auto bg-primary text-primary-foreground px-6 sm:px-8 py-3 sm:py-4 rounded-full font-medium hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 shadow-lg hover:shadow-primary/25">
                                    List Your Property
                                    <ArrowUpRight size={18} />
                                </button>
                            </Link>
                            <Link href="/owner#how-it-works">
                                <button className="text-foreground font-medium underline decoration-1 underline-offset-4 hover:text-primary transition-colors">
                                    How it works
                                </button>
                            </Link>
                        </div>

                        <div className="flex flex-col sm:flex-row items-center justify-between max-w-md pt-6 sm:pt-8 border-t border-border mt-2 mx-auto lg:mx-0 gap-6 sm:gap-0 lg:mr-10">
                            <div className="flex flex-col items-center sm:items-start text-center sm:text-left px-4">
                                <span className="text-sm text-muted-foreground font-medium mb-1">Properties Managed</span>
                                <div className="flex items-baseline gap-1">
                                    <span className="text-3xl sm:text-4xl font-bold text-foreground">2,500</span>
                                    <span className="text-3xl sm:text-4xl font-bold text-primary">+</span>
                                </div>
                                <span className="text-xs sm:text-sm text-muted-foreground">Across Dubai</span>
                            </div>

                            {/* <div className="flex flex-col gap-2 items-center sm:items-start px-4">
                                <div className="flex items-center gap-2">
                                    <span className="text-xl sm:text-2xl font-bold text-foreground">4.9</span>
                                    <div className="flex gap-0.5 text-yellow-400">
                                        {[...Array(5)].map((_, i) => (
                                            <Star key={i} size={14} className="sm:w-4 sm:h-4" fill="currentColor" />
                                        ))}
                                    </div>
                                </div>
                                <div className="flex -space-x-3">
                                    {[1, 2, 3, 4].map((i) => (
                                        <div key={i} className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-background bg-muted overflow-hidden relative">
                                            <Image
                                                src={`https://randomuser.me/api/portraits/${i % 2 === 0 ? 'women' : 'men'}/${i + 40}.jpg`}
                                                alt="Owner"
                                                fill
                                                className="object-cover"
                                            />
                                        </div>
                                    ))}
                                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-background bg-primary flex items-center justify-center text-primary-foreground z-10">
                                        <Plus size={14} className="sm:w-4 sm:h-4" />
                                    </div>
                                </div>
                            </div> */}
                        </div>
                    </div>

                    {/* Right Column: Video */}
                    <div className="relative h-[250px] sm:h-[380px] lg:h-[350px] w-full flex items-center justify-center perspective-1000 order-1 lg:order-2">
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                            className="absolute inset-0 w-full h-full"
                        >
                            {/* Custom Video Container */}
                            <div className="relative w-full h-full bg-primary/5 transition-all duration-500 rounded-3xl overflow-hidden shadow-2xl border border-border/10">
                                <video
                                    ref={videoRef}
                                    src="/video/owner.mp4"
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                    className="absolute inset-0 h-full w-full object-cover"
                                />
                                {/* Gradient overlay */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                                {/* Overlay Text inside the Video */}
                                <div className="absolute bottom-[6%] sm:bottom-[12%] left-[6%] sm:left-[10%] text-white z-10 w-full max-w-[180px] sm:max-w-md">
                                    <h3 className="text-base sm:text-2xl lg:text-4xl font-bold leading-tight mb-1 drop-shadow-xl text-white">
                                        Your Partner in <br /> Property Success
                                    </h3>
                                </div>

                                {/* Mute/Unmute Button */}
                                <button
                                    onClick={toggleMute}
                                    className="absolute bottom-6 right-6 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300 group"
                                    title={isMuted ? "Unmute" : "Mute"}
                                >
                                    {isMuted ? (
                                        <VolumeX size={20} className="group-hover:scale-110 transition-transform" />
                                    ) : (
                                        <Volume2 size={20} className="group-hover:scale-110 transition-transform" />
                                    )}
                                </button>
                            </div>


                        </motion.div>
                    </div>

                </div>
            </div>
        </section>
    );
}
